<template>
    <Page>
        <Container>
            <Loading v-if="loading" />
            <div class="mx-auto max-w-md">
                <Title>Metrics</Title>

                <div class="space-y-8" v-if="data">
                    <div>
                        <Subtitle>Revenue</Subtitle>
                        <table class="w-full">
                            <tr v-for="row in revenueRows" :key="row.key">
                                <td>{{ row.label }}</td>
                                <td class="text-right">{{ formatValue(row) }}</td>
                            </tr>
                        </table>
                    </div>

                    <div>
                        <Subtitle>Users</Subtitle>
                        <table class="w-full">
                            <tr v-for="row in userRows" :key="row.key">
                                <td>{{ row.label }}</td>
                                <td class="text-right">
                                    <span>{{ formatValue(row) }}</span>
                                    <span v-if="trendFor(row)" :class="trendClass(row)" class="ml-2 text-xs">
                                        {{ formatTrend(row) }}
                                    </span>
                                </td>
                            </tr>
                        </table>
                    </div>

                    <div>
                        <Subtitle>Workouts</Subtitle>
                        <table class="w-full">
                            <tr v-for="row in workoutRows" :key="row.key">
                                <td>{{ row.label }}</td>
                                <td class="text-right">
                                    <span>{{ formatValue(row) }}</span>
                                    <span v-if="trendFor(row)" :class="trendClass(row)" class="ml-2 text-xs">
                                        {{ formatTrend(row) }}
                                    </span>
                                </td>
                            </tr>
                        </table>
                    </div>

                    <div>
                        <Subtitle>Objects</Subtitle>
                        <table class="w-full">
                            <tr v-for="row in objectRows" :key="row.key">
                                <td>{{ row.label }}</td>
                                <td class="text-right">
                                    <span>{{ formatValue(row) }}</span>
                                    <span v-if="trendFor(row)" :class="trendClass(row)" class="ml-2 text-xs">
                                        {{ formatTrend(row) }}
                                    </span>
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>

            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue'
import {mapActions} from 'vuex';

export default defineComponent({
  name: 'Index',
  created() {
    this.loadData();
  },
  data() {
    return {
      loading: false,
      data: null,
    };
  },
  computed: {
    revenueRows() {
      return [
        {key: 'subscriptions', label: 'Subscriptions'},
        {key: 'arr', label: 'ARR', format: '$0,0'},
        {key: 'active_trials', label: 'Active Trials'},
        {key: 'expired_trials', label: 'Expired Trials'},
      ];
    },
    userRows() {
      return [
        {key: 'total_users', label: 'Total Users'},
        {key: 'new_users_7d', label: 'New Users 7d'},
        {key: 'new_users_30d', label: 'New Users 30d'},
        {key: 'active_users_7d', label: 'Active Users 7d'},
        {key: 'active_users_30d', label: 'Active Users 30d'},
      ];
    },
    workoutRows() {
      return [
        {key: 'total_workouts', label: 'Total Workouts'},
        {key: 'workouts_24h', label: 'Workouts 24h'},
        {key: 'workouts_7d', label: 'Workouts 7d'},
        {key: 'workouts_30d', label: 'Workouts 30d'},
        {key: 'workout_users_30d', label: 'Workout Users 30d'},
        {key: 'avg_workouts_per_active_user_30d', label: 'Avg Workouts / User 30d', format: '0,0.0', decimals: 1},
      ];
    },
    objectRows() {
      return [
        {key: 'templates', label: 'Templates'},
        {key: 'movements', label: 'Movements'},
        {key: 'bands', label: 'Bands'},
        {key: 'exercises_7d', label: 'Exercises 7d'},
        {key: 'exercises_30d', label: 'Exercises 30d'},
        {key: 'log_entries_30d', label: 'Log Entries 30d'},
      ];
    },
  },
  methods: {
    ...mapActions({
      adminIndex: 'admin/index',
    }),
    loadData() {
        this.loading = true;
        return this.adminIndex().then((response) => {
          this.data = response.data.data;
        }).finally(() => {
          this.loading = false;
        });
    },
    formatValue(row) {
      return this.$numeral(this.data[row.key] || 0).format(row.format || '0,0');
    },
    trendFor(row) {
      return this.data?.trends?.[row.key] || null;
    },
    trendClass(row) {
      const change = this.trendFor(row)?.change || 0;

      if (change > 0) {
        return 'text-green-500';
      }

      if (change < 0) {
        return 'text-red-500';
      }

      return 'text-gray-400';
    },
    formatTrend(row) {
      const trend = this.trendFor(row);

      if (!trend) {
        return '';
      }

      if (trend.change_percent !== null) {
        const prefix = trend.change_percent > 0 ? '+' : '';
        return `${prefix}${this.$numeral(trend.change_percent).format('0,0')}%`;
      }

      const format = row.decimals ? '0,0.0' : '0,0';
      const prefix = trend.change > 0 ? '+' : '';

      return `${prefix}${this.$numeral(trend.change).format(format)}`;
    },
  },
})
</script>

<style scoped lang="scss">

</style>
